/** evLoss below this (bb) counts as "chose best" → excellent (§7.3 "loss ≈ 0"). */
const EXCELLENT_EPS = 0.05;
const BANDS = [
    { band: 'excellent', base: 10, min: 10, maxLoss: EXCELLENT_EPS },
    { band: 'good', base: 9, min: 8, maxLoss: 0.25 },
    { band: 'acceptable', base: 7, min: 6, maxLoss: 1.0 },
    { band: 'marginal', base: 5, min: 4, maxLoss: 3.0 },
    { band: 'bad', base: 3, min: 2, maxLoss: 8.0 },
    { band: 'punt', base: 1, min: 1, maxLoss: Infinity },
];
/** The band + its base/min for an evLoss in bb. Loss is clamped at 0 (a negative loss is "chose best"). */
export const bandFromEvLoss = (evLossBB) => {
    const loss = Math.max(0, evLossBB);
    for (const b of BANDS)
        if (loss <= b.maxLoss)
            return b;
    return BANDS[BANDS.length - 1]; // unreachable (punt has Infinity), but keeps the type total.
};
/** Score → band (the inverse map, for reporting the modified score's band). */
export const bandForScore = (score) => {
    if (score >= 10)
        return 'excellent';
    if (score >= 8)
        return 'good';
    if (score >= 6)
        return 'acceptable';
    if (score >= 4)
        return 'marginal';
    if (score >= 2)
        return 'bad';
    return 'punt';
};
/** The arithmetic grade modifier (§7.3): exact +0, close −1, off −2. Absent verdict = no modifier. */
export const arithmeticModifier = (v) => v === 'close' ? -1 : v === 'off' ? -2 : 0;
/** The sizing grade modifier (§7.3): off by one step −1, off by 2+ steps −2, otherwise 0. */
export const sizingModifier = (steps) => (steps <= 0 ? 0 : steps === 1 ? -1 : -2);
/**
 * The full §7.3 grade for an evLoss + modifiers. Base score from the band; sizing + arithmetic
 * modifiers subtract; the forbidden-vs-high-confidence override forces punt; the result is floored at
 * (band minimum − 2) and clamped to [1, 10]. Returns the numeric score and its band.
 */
export const gradeFromEvLoss = (evLossBB, mods = {}) => {
    if (mods.forbiddenHighConfidence)
        return { score: 1, band: 'punt' };
    const def = bandFromEvLoss(evLossBB);
    const delta = sizingModifier(mods.sizingSteps ?? 0) + arithmeticModifier(mods.arithmeticVerdict);
    const floor = Math.max(1, def.min - 2);
    const score = Math.max(floor, Math.min(10, def.base + delta));
    return { score, band: bandForScore(score) };
};
/**
 * Grade an ABSTENTION (§7.2): the one-street EV model declined to rank, so there is no evLoss. Grade
 * on rule/principle compliance instead - `matchedFinal` = did the user pick the recommended line;
 * `plausible` = was it at least a listed alternative. Modifiers still apply. Forbidden-vs-high-conf
 * still punts.
 */
export const gradeAbstention = (matchedFinal, plausible, mods = {}) => {
    if (mods.forbiddenHighConfidence)
        return { score: 1, band: 'punt' };
    // compliance base: matched the read-driven line → acceptable(7); a plausible alternative →
    // marginal(5); neither → bad(3). Conservative: without EV we never award excellent/good.
    const base = matchedFinal ? 7 : plausible ? 5 : 3;
    const min = matchedFinal ? 6 : plausible ? 4 : 2;
    const delta = sizingModifier(mods.sizingSteps ?? 0) + arithmeticModifier(mods.arithmeticVerdict);
    const floor = Math.max(1, min - 2);
    const score = Math.max(floor, Math.min(10, base + delta));
    return { score, band: bandForScore(score) };
};
//# sourceMappingURL=bands.js.map